import { FirebaseService, ToastService } from "@shared/lib/services";
import { IBrand } from "@shared/lib/types";
import {
  Button,
  Form,
  Image,
  Input,
  Popconfirm,
  Row,
  Select,
  Upload,
} from "antd";
import { useState } from "react";

import styles from "./styles.module.scss";

const { Option } = Select;

interface BrandFormProps {
  onSubmit: (values: Partial<IBrand>) => void;
  isSubmitting: boolean;
  initialValues: IBrand;
}

export function BrandUpdateForm({
  onSubmit,
  isSubmitting,
  initialValues,
}: BrandFormProps) {
  const [form] = Form.useForm();
  const [profilePicUrl, setProfilePicUrl] = useState(initialValues.logoUrl);

  const handleImageUpload = async (file: File) => {
    try {
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        "profile-pictures",
        file
      );
      setProfilePicUrl(uploadedUrl);

      form.setFieldValue("logo", uploadedUrl);
    } catch (error) {
      ToastService.error("Image upload failed. Please try again.");
    }
  };

  const handleDeleteImage = () => {
    setProfilePicUrl("");
    ToastService.success("Profile picture removed successfully.");
    form.setFieldValue("logo", "");
  };

  const handleSubmit = (values: any) => {
    onSubmit({
      ...values,
      logoUrl: profilePicUrl,
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      className={styles.form}
      initialValues={{
        logoUrl: profilePicUrl,
        name: initialValues.name,
        companyName: initialValues.companyName,
        // type: initialValues.type,
        websiteUrl: initialValues.websiteUrl,
      }}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
    >
      <div className={styles.content}>
        <Form.Item
          label="Brand Logo"
          name="logoUrl"
          rules={[{ required: true, message: "Brand logo is required" }]}
        >
          <Upload
            onChange={(info) =>
              info.file && handleImageUpload(info.file.originFileObj!)
            }
            showUploadList={false}
            accept="image/jpeg,image/jpg,image/png"
            beforeUpload={(file) => {
              const isImage = ["image/jpeg", "image/jpg", "image/png"].includes(
                file.type
              );
              if (!isImage) {
                ToastService.error(
                  "You can only upload JPG, JPEG, or PNG files!"
                );
              }
              return isImage; // Prevents upload if not a valid image
            }}
          >
            <Button> Upload</Button>
          </Upload>
          {profilePicUrl && (
            <div style={{ marginTop: "16px" }}>
              <Image src={profilePicUrl} alt="Profile" width={60} height={60} />
              <Popconfirm
                title="Are you sure to delete this image?"
                onConfirm={handleDeleteImage}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" danger>
                  Delete Image
                </Button>
              </Popconfirm>
            </div>
          )}
        </Form.Item>
        <Form.Item
          name="name"
          label="Brand Name"
          rules={[{ required: true, message: "Brand name is required" }]}
        >
          <Input placeholder="Brand Name" />
        </Form.Item>

        <Form.Item
          name="companyName"
          label="Company Name"
          rules={[{ required: true, message: "Company name is required" }]}
        >
          <Input placeholder="Company Name" />
        </Form.Item>

        {/* <Form.Item
          name="type"
          label="Brand Type"
          rules={[{ required: true, message: "Brand type  is required" }]}
        >
          <Select placeholder="Select Brand Type">
            {Object.values(BrandType).map((status) => (
              <Option key={status} value={status}>
                {status}
              </Option>
            ))}
          </Select>
        </Form.Item> */}
        <Form.Item name="websiteUrl" label="Website URL">
          <Input placeholder="Website URL" />
        </Form.Item>
      </div>

      <Form.Item>
        <Row justify={"end"}>
          {" "}
          <Button
            type="primary"
            htmlType="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Row>
      </Form.Item>
    </Form>
  );
}
